import { useState, useMemo } from 'react';
import { Users, Sparkles, Plus, X, ChevronDown, ChevronUp, Check, Loader2, Filter, ArrowLeftRight, AlertTriangle, ShieldAlert } from 'lucide-react';
import clsx from 'clsx';
import {
  generateSmartSuggestions,
  getSectionSuggestions,
  AssignmentScore,
  ScoringContext,
} from '../../utils/assignmentScoring';
import type { Section, Employee, Guest, Reservation } from '../../hooks/useShiftSetupData';
import type { ShiftAssignment } from '../../services/api';

interface SmartAssignmentPanelProps {
  sections: Section[];
  employees: Employee[];
  assignments: ShiftAssignment[];
  reservations: Reservation[];
  guests: Guest[];
  onAssign: (employeeId: string, role: string, sectionId?: string, tableIds?: string[]) => Promise<void>;
  onUnassign: (assignmentId: string) => Promise<void>;
  onUpdateSection?: (assignmentId: string, sectionId: string | null, tableIds?: string[]) => Promise<void>;
}

const ROLE_LABELS: Record<string, string> = {
  server: 'Server',
  bartender: 'Bartender',
  host: 'Host',
  cook: 'Cook',
  manager: 'Manager',
  busser: 'Busser',
  dishwasher: 'Dishwasher',
};

const FOH_ROLES = ['server', 'bartender', 'host', 'busser', 'manager'];

const SKILL_LABELS: Record<string, string> = {
  vip_handling: 'VIP Handling',
  allergy_certified: 'Allergy Certified',
  wine_certified: 'Wine Certified',
  bar_trained: 'Bar Trained',
  large_parties: 'Large Parties',
  fine_dining: 'Fine Dining',
};

export default function SmartAssignmentPanel({
  sections,
  employees,
  assignments,
  reservations,
  guests,
  onAssign,
  onUnassign,
  onUpdateSection,
}: SmartAssignmentPanelProps) {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [isApplyingAll, setIsApplyingAll] = useState(false);
  const [assigningSection, setAssigningSection] = useState<string | null>(null);
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [skillFilter, setSkillFilter] = useState<string>('all');
  const [swapMode, setSwapMode] = useState<{ fromSectionId: string; fromAssignmentId: string; employeeId: string } | null>(null);
  const [editingSectionFor, setEditingSectionFor] = useState<string | null>(null);
  const [editingTablesFor, setEditingTablesFor] = useState<string | null>(null);
  const [selectedTableIds, setSelectedTableIds] = useState<string[]>([]);
  const [assigningToSection, setAssigningToSection] = useState<string | null>(null);
  const [pendingAssignment, setPendingAssignment] = useState<{ employeeId: string; sectionId: string; tableIds: string[] } | null>(null);

  // Build scoring context
  const scoringContext: ScoringContext = {
    employees: employees.map((e) => ({
      ...e,
      skills: e.skills || [],
      allergyTraining: e.allergyTraining || [],
      sectionHistory: e.sectionHistory || {},
    })),
    sections: sections.map((s) => ({
      ...s,
      tableIds: s.tables?.map((t) => t.id) || [],
    })),
    reservations,
    guests,
    existingAssignments: assignments.map((a) => ({
      employeeId: a.employeeId,
      sectionId: a.sectionId,
    })),
  };

  // Generate smart suggestions
  const suggestions = generateSmartSuggestions(scoringContext);

  // Map assignments by section
  const assignmentsBySection: Record<string, ShiftAssignment> = {};
  for (const assignment of assignments) {
    if (assignment.sectionId) {
      assignmentsBySection[assignment.sectionId] = assignment;
    }
  }

  // Apply all suggestions at once
  const handleApplyAll = async () => {
    setIsApplyingAll(true);
    try {
      for (const [sectionId, suggestion] of suggestions) {
        const employee = employees.find((e) => e.id === suggestion.employeeId);
        if (employee) {
          await onAssign(employee.id, employee.primaryRole, sectionId);
        }
      }
    } finally {
      setIsApplyingAll(false);
    }
  };

  // Apply single suggestion - now shows table selection first
  const handleApplySuggestion = (sectionId: string, suggestion: AssignmentScore) => {
    const section = sections.find(s => s.id === sectionId);
    // If section has tables, show table selection step
    if (section?.tables && section.tables.length > 0) {
      setPendingAssignment({ employeeId: suggestion.employeeId, sectionId, tableIds: section.tables.map(t => t.id) });
      setAssigningToSection(sectionId);
    } else {
      // No tables, assign directly
      completeAssignment(suggestion.employeeId, sectionId, []);
    }
  };

  // Manual assignment - now shows table selection first
  const handleManualAssign = (employeeId: string, sectionId: string) => {
    const section = sections.find(s => s.id === sectionId);
    // If section has tables, show table selection step
    if (section?.tables && section.tables.length > 0) {
      setPendingAssignment({ employeeId, sectionId, tableIds: section.tables.map(t => t.id) }); // Default to all tables
      setAssigningToSection(sectionId);
      setShowEmployeeDropdown(null);
    } else {
      // No tables, assign directly
      completeAssignment(employeeId, sectionId, []);
    }
  };

  // Complete the assignment with selected tables
  const completeAssignment = async (employeeId: string, sectionId: string, tableIds: string[]) => {
    setAssigningSection(sectionId);
    try {
      const employee = employees.find((e) => e.id === employeeId);
      if (employee) {
        await onAssign(employee.id, employee.primaryRole, sectionId, tableIds);
      }
    } finally {
      setAssigningSection(null);
      setShowEmployeeDropdown(null);
      setPendingAssignment(null);
      setAssigningToSection(null);
    }
  };

  // Toggle table in pending assignment
  const togglePendingTable = (tableId: string) => {
    if (!pendingAssignment) return;
    const newTableIds = pendingAssignment.tableIds.includes(tableId)
      ? pendingAssignment.tableIds.filter(id => id !== tableId)
      : [...pendingAssignment.tableIds, tableId];
    setPendingAssignment({ ...pendingAssignment, tableIds: newTableIds });
  };

  // Cancel pending assignment
  const cancelPendingAssignment = () => {
    setPendingAssignment(null);
    setAssigningToSection(null);
  };

  // Get available employees (not already assigned)
  const assignedEmployeeIds = new Set(assignments.map((a) => a.employeeId));
  const availableEmployees = employees.filter(
    (e) => !assignedEmployeeIds.has(e.id) && FOH_ROLES.includes(e.primaryRole)
  );

  // Filter employees based on role and skill filters
  const filteredAvailableEmployees = useMemo(() => {
    return availableEmployees.filter((emp) => {
      // Role filter
      if (roleFilter !== 'all' && emp.primaryRole !== roleFilter) {
        return false;
      }
      // Skill filter
      if (skillFilter !== 'all') {
        const empSkills = emp.skills || [];
        if (!empSkills.includes(skillFilter)) {
          return false;
        }
      }
      return true;
    });
  }, [availableEmployees, roleFilter, skillFilter]);

  // Get unique skills from all employees for filter dropdown
  const availableSkills = useMemo(() => {
    const skills = new Set<string>();
    for (const emp of employees) {
      for (const skill of emp.skills || []) {
        skills.add(skill);
      }
    }
    return Array.from(skills);
  }, [employees]);

  // Calculate workload per section based on reservations
  const sectionWorkload = useMemo(() => {
    const workload: Record<string, number> = {};
    for (const section of sections) {
      // Count reservations for tables in this section
      const tableIds = new Set(section.tables?.map(t => t.id) || []);
      workload[section.id] = reservations.filter(r => r.tableId && tableIds.has(r.tableId)).length;
    }
    return workload;
  }, [sections, reservations]);

  const maxWorkload = Math.max(...Object.values(sectionWorkload), 1);

  // Handle swap between sections
  const handleSwap = async (toSectionId: string, toAssignmentId: string) => {
    if (!swapMode) return;

    const toAssignment = assignments.find(a => a.id === toAssignmentId);
    const fromEmployee = employees.find(e => e.id === swapMode.employeeId);
    const toEmployee = toAssignment?.employee;

    if (!toEmployee || !fromEmployee) return;

    setAssigningSection(swapMode.fromSectionId);
    try {
      // Remove both assignments
      await onUnassign(swapMode.fromAssignmentId);
      await onUnassign(toAssignmentId);

      // Re-assign in swapped positions
      await onAssign(toEmployee.id, toEmployee.primaryRole || 'server', swapMode.fromSectionId);
      await onAssign(fromEmployee.id, fromEmployee.primaryRole, toSectionId);
    } finally {
      setAssigningSection(null);
      setSwapMode(null);
    }
  };

  const cancelSwap = () => {
    setSwapMode(null);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-3 sm:px-4 py-3 border-b border-slate-200 bg-slate-50/50">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900 uppercase tracking-wide">
            <Users className="w-4 h-4" />
            Staff Assignments
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={clsx(
                'flex items-center gap-1 px-2.5 py-2 text-xs font-medium rounded-lg border transition-colors touch-manipulation',
                showFilters
                  ? 'bg-indigo-100 text-indigo-700 border-indigo-200'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50 active:bg-slate-100'
              )}
            >
              <Filter className="w-3.5 h-3.5" />
              Filters
            </button>
            {suggestions.size > 0 && (
              <button
                onClick={handleApplyAll}
                disabled={isApplyingAll}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 active:bg-amber-200 transition-colors disabled:opacity-50 touch-manipulation"
              >
                {isApplyingAll ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Sparkles className="w-3.5 h-3.5" />
                )}
                <span className="hidden sm:inline">Smart Suggest</span> All
              </button>
            )}
          </div>
        </div>

        {/* Swap Mode Banner */}
        {swapMode && (
          <div className="mt-3 p-2 bg-indigo-50 border border-indigo-200 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-indigo-700">
              <ArrowLeftRight className="w-4 h-4" />
              <span>
                Select another section to swap with{' '}
                <strong>{employees.find(e => e.id === swapMode.employeeId)?.firstName}</strong>
              </span>
            </div>
            <button
              onClick={cancelSwap}
              className="text-indigo-600 hover:text-indigo-800 text-xs font-medium"
            >
              Cancel
            </button>
          </div>
        )}

        {/* Filter Controls */}
        {showFilters && (
          <div className="mt-3 p-3 bg-white border border-slate-200 rounded-lg space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Role</label>
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="all">All Roles</option>
                  {FOH_ROLES.map((role) => (
                    <option key={role} value={role}>{ROLE_LABELS[role] || role}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Skill</label>
                <select
                  value={skillFilter}
                  onChange={(e) => setSkillFilter(e.target.value)}
                  className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="all">All Skills</option>
                  {availableSkills.map((skill) => (
                    <option key={skill} value={skill}>{SKILL_LABELS[skill] || skill}</option>
                  ))}
                </select>
              </div>
            </div>
            {(roleFilter !== 'all' || skillFilter !== 'all') && (
              <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                <p className="text-xs text-slate-500">
                  {filteredAvailableEmployees.length} staff match filters
                </p>
                <button
                  onClick={() => { setRoleFilter('all'); setSkillFilter('all'); }}
                  className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="divide-y divide-slate-100">
        {sections.map((section) => {
          const assignment = assignmentsBySection[section.id];
          const suggestion = suggestions.get(section.id);
          const isExpanded = expandedSection === section.id;
          const isAssigning = assigningSection === section.id;
          const workload = sectionWorkload[section.id] || 0;
          const workloadPct = (workload / maxWorkload) * 100;
          const isSwapTarget = swapMode && swapMode.fromSectionId !== section.id && assignment;

          return (
            <div
              key={section.id}
              className={clsx(
                'p-4',
                isSwapTarget && 'bg-indigo-50/50 border-l-4 border-l-indigo-400'
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: section.color || '#3B82F6' }}
                  />
                  <div className="min-w-0">
                    <span className="font-medium text-slate-900">{section.name}</span>
                    {/* Workload indicator */}
                    <div className="flex items-center gap-2 mt-1">
                      <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={clsx(
                            'h-full rounded-full transition-all',
                            workloadPct > 66 ? 'bg-amber-500' : workloadPct > 33 ? 'bg-emerald-400' : 'bg-slate-300'
                          )}
                          style={{ width: `${workloadPct}%` }}
                        />
                      </div>
                      <span className="text-[10px] text-slate-400">{workload} res.</span>
                    </div>
                  </div>
                </div>

                {/* Swap target mode */}
                {isSwapTarget && (
                  <button
                    onClick={() => handleSwap(section.id, assignment.id)}
                    disabled={isAssigning}
                    className="flex items-center gap-2 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50"
                  >
                    {isAssigning ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <ArrowLeftRight className="w-4 h-4" />
                    )}
                    Swap with {assignment.employee?.firstName}
                  </button>
                )}

                {/* Normal view when not in swap target mode */}
                {!isSwapTarget && assignment ? (
                  // Has assignment - show employee with section/table editing
                  <div className="flex items-center gap-2">
                    {editingSectionFor === assignment.id && onUpdateSection ? (
                      // Edit mode - show section dropdown and table selection
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <select
                            value={assignment.sectionId || ''}
                            onChange={async (e) => {
                              setAssigningSection(section.id);
                              try {
                                await onUpdateSection(assignment.id, e.target.value || null, []);
                                setSelectedTableIds([]);
                              } finally {
                                setAssigningSection(null);
                              }
                            }}
                            disabled={isAssigning}
                            className="px-2 py-1.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                          >
                            <option value="">No Section</option>
                            {sections.map((s) => (
                              <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => {
                              setEditingSectionFor(null);
                              setSelectedTableIds([]);
                            }}
                            className="p-1 text-slate-400 hover:text-slate-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        {/* Table selection for current section */}
                        {section.tables && section.tables.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {section.tables.map((table) => {
                              const isSelected = (assignment.tableIds || []).includes(table.id) ||
                                selectedTableIds.includes(table.id);
                              return (
                                <button
                                  key={table.id}
                                  onClick={async () => {
                                    const newTableIds = isSelected
                                      ? (assignment.tableIds || []).filter((id) => id !== table.id)
                                      : [...(assignment.tableIds || []), table.id];
                                    setAssigningSection(section.id);
                                    try {
                                      await onUpdateSection(assignment.id, assignment.sectionId || null, newTableIds);
                                    } finally {
                                      setAssigningSection(null);
                                    }
                                  }}
                                  disabled={isAssigning}
                                  className={clsx(
                                    'px-2 py-1 text-xs rounded border transition-colors',
                                    isSelected
                                      ? 'bg-indigo-100 border-indigo-300 text-indigo-700'
                                      : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                                  )}
                                >
                                  {table.number}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ) : (
                      // Normal view - show employee chip with assigned tables
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <div
                            className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg cursor-pointer hover:bg-emerald-100 transition-colors"
                            onClick={() => {
                              if (onUpdateSection) {
                                setEditingSectionFor(assignment.id);
                                setSelectedTableIds(assignment.tableIds || []);
                              }
                            }}
                            title={onUpdateSection ? "Click to edit section/tables" : undefined}
                          >
                            <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center text-white text-xs font-medium">
                              {assignment.employee?.firstName?.[0]}
                              {assignment.employee?.lastName?.[0]}
                            </div>
                            <span className="text-sm font-medium text-emerald-700">
                              {assignment.employee?.firstName} {assignment.employee?.lastName?.[0]}.
                            </span>
                          </div>
                          {!swapMode && (
                            <>
                              <button
                                onClick={() => setSwapMode({ fromSectionId: section.id, fromAssignmentId: assignment.id, employeeId: assignment.employeeId })}
                                className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                title="Swap with another section"
                              >
                                <ArrowLeftRight className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => onUnassign(assignment.id)}
                                className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                title="Remove assignment"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                        {/* Show assigned tables */}
                        {section.tables && section.tables.length > 0 && (
                          <div className="flex flex-wrap items-center gap-1 ml-1">
                            {assignment.tableIds && assignment.tableIds.length === section.tables.length ? (
                              <span className="px-2 py-0.5 text-[11px] bg-emerald-100 text-emerald-700 rounded-full font-medium">
                                All {section.tables.length} tables
                              </span>
                            ) : assignment.tableIds && assignment.tableIds.length > 0 ? (
                              <>
                                <span className="text-[10px] text-slate-400 mr-1">Tables:</span>
                                {assignment.tableIds.map((tableId) => {
                                  const table = section.tables?.find((t) => t.id === tableId);
                                  return table ? (
                                    <span
                                      key={tableId}
                                      className="px-1.5 py-0.5 text-[10px] bg-emerald-100 text-emerald-700 rounded font-medium"
                                    >
                                      {table.number}
                                    </span>
                                  ) : null;
                                })}
                              </>
                            ) : (
                              <span className="px-2 py-0.5 text-[10px] bg-amber-100 text-amber-700 rounded-full">
                                No tables assigned
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : !isSwapTarget && !assignment ? (
                  // No assignment - show suggestion or assign button
                  <div className="flex items-center gap-2">
                    {suggestion && (() => {
                      const suggestedEmp = employees.find((e) => e.id === suggestion.employeeId);
                      const suggestedHasWarnings = suggestedEmp?.hasComplianceIssues ||
                        (suggestedEmp?.complianceWarnings && suggestedEmp.complianceWarnings.length > 0) ||
                        (suggestedEmp?.complianceBlockingReasons && suggestedEmp.complianceBlockingReasons.length > 0);
                      const suggestedWarnings = [
                        ...(suggestedEmp?.complianceBlockingReasons || []),
                        ...(suggestedEmp?.complianceWarnings || []),
                      ];

                      return (
                        <button
                          onClick={() => handleApplySuggestion(section.id, suggestion)}
                          disabled={isAssigning}
                          className={clsx(
                            "flex items-center gap-2 px-3 py-1.5 border rounded-lg transition-colors disabled:opacity-50",
                            suggestedHasWarnings
                              ? "bg-amber-50 border-amber-300 hover:bg-amber-100"
                              : "bg-amber-50 border-amber-200 hover:bg-amber-100"
                          )}
                          title={suggestedHasWarnings ? suggestedWarnings.join('\n') : undefined}
                        >
                          {isAssigning ? (
                            <Loader2 className="w-4 h-4 animate-spin text-amber-600" />
                          ) : (
                            <Sparkles className="w-4 h-4 text-amber-500" />
                          )}
                          <span className="text-sm font-medium text-amber-700">
                            {suggestedEmp?.firstName}
                          </span>
                          {suggestedHasWarnings && (
                            <ShieldAlert className="w-3.5 h-3.5 text-amber-500" />
                          )}
                          <span className="text-xs text-amber-500">
                            ({suggestion.totalScore}pts)
                          </span>
                        </button>
                      );
                    })()}
                    <div className="relative">
                      <button
                        onClick={() =>
                          setShowEmployeeDropdown(
                            showEmployeeDropdown === section.id ? null : section.id
                          )
                        }
                        className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        Assign
                      </button>
                      {showEmployeeDropdown === section.id && (
                        <div className="absolute right-0 top-full mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-lg z-10 max-h-64 overflow-y-auto">
                          {filteredAvailableEmployees.length === 0 ? (
                            <p className="p-3 text-sm text-slate-500 text-center">
                              {(roleFilter !== 'all' || skillFilter !== 'all')
                                ? 'No staff match filters'
                                : 'No available staff'}
                            </p>
                          ) : (
                            filteredAvailableEmployees.map((emp) => {
                              const hasWarnings = emp.hasComplianceIssues ||
                                (emp.complianceWarnings && emp.complianceWarnings.length > 0) ||
                                (emp.complianceBlockingReasons && emp.complianceBlockingReasons.length > 0);
                              const allWarnings = [
                                ...(emp.complianceBlockingReasons || []),
                                ...(emp.complianceWarnings || []),
                              ];

                              return (
                                <button
                                  key={emp.id}
                                  onClick={() => handleManualAssign(emp.id, section.id)}
                                  className={clsx(
                                    "w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 text-left transition-colors border-b border-slate-50 last:border-0",
                                    hasWarnings && "bg-amber-50/50"
                                  )}
                                >
                                  <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 text-xs font-medium flex-shrink-0">
                                    {emp.firstName?.[0]}
                                    {emp.lastName?.[0]}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5">
                                      <p className="text-sm font-medium text-slate-900 truncate">
                                        {emp.firstName} {emp.lastName}
                                      </p>
                                      {hasWarnings && (
                                        <span
                                          className="text-amber-500 flex-shrink-0"
                                          title={allWarnings.join('\n')}
                                        >
                                          <ShieldAlert className="w-3.5 h-3.5" />
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-1 flex-wrap">
                                      <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">
                                        {ROLE_LABELS[emp.primaryRole] || emp.primaryRole}
                                      </span>
                                      {emp.skills?.slice(0, 2).map(skill => (
                                        <span key={skill} className="text-[10px] px-1.5 py-0.5 bg-indigo-50 text-indigo-600 rounded">
                                          {SKILL_LABELS[skill] || skill}
                                        </span>
                                      ))}
                                    </div>
                                    {/* Compliance warnings */}
                                    {hasWarnings && (
                                      <div className="mt-1 space-y-0.5">
                                        {allWarnings.slice(0, 2).map((warning, idx) => (
                                          <p key={idx} className="text-[10px] text-amber-600 truncate">
                                            ⚠ {warning}
                                          </p>
                                        ))}
                                        {allWarnings.length > 2 && (
                                          <p className="text-[10px] text-amber-500">
                                            +{allWarnings.length - 2} more warnings
                                          </p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </button>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => setExpandedSection(isExpanded ? null : section.id)}
                      className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                ) : null}
              </div>

              {/* Expanded view with suggestions details */}
              {isExpanded && suggestion && !assignment && !pendingAssignment && (
                <div className="mt-3 p-3 bg-amber-50/50 rounded-lg border border-amber-100">
                  <p className="text-xs font-medium text-amber-700 mb-2">
                    Why {employees.find((e) => e.id === suggestion.employeeId)?.firstName}?
                  </p>
                  <ul className="space-y-1">
                    {suggestion.reasons.map((reason, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-amber-600">
                        <Check className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        {reason}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-2 pt-2 border-t border-amber-200">
                    <p className="text-xs text-amber-600">
                      Score breakdown: VIP {suggestion.breakdown.vipPreference}, Allergy{' '}
                      {suggestion.breakdown.allergyAwareness}, Experience{' '}
                      {suggestion.breakdown.sectionExperience}, Workload{' '}
                      {suggestion.breakdown.workloadBalance}, Skill{' '}
                      {suggestion.breakdown.skillMatch}
                    </p>
                  </div>
                </div>
              )}

              {/* Table Selection Step - shown when assigning to this section */}
              {pendingAssignment && pendingAssignment.sectionId === section.id && section.tables && section.tables.length > 0 && (
                <div className="mt-3 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-indigo-500 flex items-center justify-center text-white text-xs font-medium">
                        {employees.find(e => e.id === pendingAssignment.employeeId)?.firstName?.[0]}
                        {employees.find(e => e.id === pendingAssignment.employeeId)?.lastName?.[0]}
                      </div>
                      <span className="text-sm font-medium text-indigo-900">
                        Select tables for {employees.find(e => e.id === pendingAssignment.employeeId)?.firstName}
                      </span>
                    </div>
                    <button
                      onClick={cancelPendingAssignment}
                      className="p-1 text-indigo-400 hover:text-indigo-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {section.tables.map((table) => {
                      const isSelected = pendingAssignment.tableIds.includes(table.id);
                      return (
                        <button
                          key={table.id}
                          onClick={() => togglePendingTable(table.id)}
                          className={clsx(
                            'px-3 py-2 text-sm font-medium rounded-lg border-2 transition-all',
                            isSelected
                              ? 'bg-indigo-600 border-indigo-600 text-white shadow-sm'
                              : 'bg-white border-slate-200 text-slate-600 hover:border-indigo-300 hover:bg-indigo-50'
                          )}
                        >
                          <span className="flex items-center gap-1.5">
                            {isSelected && <Check className="w-3.5 h-3.5" />}
                            Table {table.number}
                            <span className="text-xs opacity-75">({table.capacity} seats)</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex gap-2">
                      <button
                        onClick={() => setPendingAssignment({ ...pendingAssignment, tableIds: section.tables!.map(t => t.id) })}
                        className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                      >
                        Select All
                      </button>
                      <span className="text-slate-300">|</span>
                      <button
                        onClick={() => setPendingAssignment({ ...pendingAssignment, tableIds: [] })}
                        className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                      >
                        Clear All
                      </button>
                    </div>
                    <button
                      onClick={() => completeAssignment(pendingAssignment.employeeId, pendingAssignment.sectionId, pendingAssignment.tableIds)}
                      disabled={isAssigning}
                      className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50"
                    >
                      {isAssigning ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Check className="w-4 h-4" />
                      )}
                      Assign {pendingAssignment.tableIds.length} {pendingAssignment.tableIds.length === 1 ? 'Table' : 'Tables'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {sections.length === 0 && (
          <div className="p-8 text-center">
            <Users className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No sections configured for this shift</p>
          </div>
        )}
      </div>
    </div>
  );
}
